/* iter-53m — Post-synopsis-import Claude offer dialog.
 *
 * When a user imports a synopsis WITHOUT enabling Auto-draft, the
 * project is scaffolded (outline only). Previously the user then had
 * to navigate to Chapter 1 and click Generate manually — most don't.
 * This modal shows immediately after the import success and offers:
 *
 *   1. Draft Chapter 1 only — cheapest path, ~30s, lets the user
 *      iterate the opening before committing to a full draft.
 *   2. Build the full first draft — calls /api/build-novel for every
 *      planned chapter. Long-running (~60-120s/chapter).
 *   3. Just leave the outline — user dismisses, writes manually.
 *
 * The third option preserves the existing behaviour for power users.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, BookOpenText, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { generateApi } from '@/lib/api';
import { usePW } from '@/lib/store';

export default function SynopsisFollowupDialog({ open, onOpenChange, project, synopsis }) {
  const { rebuildNovel, loadProject } = usePW();
  const [busy, setBusy] = useState(null);  // 'chapter1' | 'full' | null

  const draftChapterOne = async () => {
    if (!project) return;
    setBusy('chapter1');
    try {
      // Call build-novel restricted to chapter 1 by passing
      // include_existing=false + a short guidance bias.
      // Falls back to client-side rebuildNovel if the project store
      // exposes the helper.
      await generateApi.build({
        project_id:       project.id,
        user_guidance:    synopsis || '',
        include_existing: false,
        only_first_chapter: true,
      });
      toast.success('Chapter 1 drafted. Edit it in the writing pane.');
      await loadProject(project.id, { silent: true });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chapter 1 draft failed.');
    } finally { setBusy(null); }
  };

  const buildFullNovel = async () => {
    setBusy('full');
    try {
      await rebuildNovel({ guidance: synopsis || '', includeExisting: false });
      // rebuildNovel handles its own toasts + state refresh.
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Full draft failed.');
    } finally { setBusy(null); }
  };

  const dismiss = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="synopsis-followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Your outline is ready
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Claude scaffolded {project?.chapters_count || ''} chapter
            {project?.chapters_count === 1 ? '' : 's'} from your synopsis.
            Want it to keep going?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 py-2">
          <button onClick={draftChapterOne} disabled={!!busy}
                  data-testid="synopsis-followup-chapter1"
                  className="group flex w-full items-start gap-3 rounded-md border border-border bg-card p-3 text-left hover:border-primary hover:bg-primary/5 disabled:opacity-50">
            <Edit className="mt-0.5 h-4 w-4 text-primary" />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-[13px] font-semibold">
                Draft Chapter 1 only
                {busy === 'chapter1' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </div>
              <div className="text-[11.5px] text-muted-foreground">
                Fast (~30 seconds). Lets you read &amp; edit the opening before
                committing to the full novel.
              </div>
            </div>
          </button>

          <button onClick={buildFullNovel} disabled={!!busy}
                  data-testid="synopsis-followup-full"
                  className="group flex w-full items-start gap-3 rounded-md border border-border bg-card p-3 text-left hover:border-primary hover:bg-primary/5 disabled:opacity-50">
            <BookOpenText className="mt-0.5 h-4 w-4 text-primary" />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-[13px] font-semibold">
                Build the full first draft
                {busy === 'full' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </div>
              <div className="text-[11.5px] text-muted-foreground">
                Drafts every planned chapter end-to-end. Long-running
                (~1–2 min per chapter); flips the project to Draft mode
                when finished. Watch the progress in the build panel.
              </div>
            </div>
          </button>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={dismiss} disabled={!!busy}
                  data-testid="synopsis-followup-skip">
            Just leave the outline — I'll write it myself
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
